/**
 * Phase 7 Slice E — Suggested-next-action card.
 *
 * Renders up to 3 visible suggestion cards under the cockpit (5 max
 * with "+ N more" expander). Each card has a severity-colored left
 * border, localized headline (DE+EN — server-side), an evidence chip,
 * and two actions: "Apply" (routes to Autopilot) and "Dismiss"
 * (with optional reason). See ADR-0036.
 */
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Lightbulb, ChevronDown, ChevronUp, Check, X,
    Sparkles, AlertTriangle, AlertOctagon, RefreshCw, Loader2,
    ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useT, useLocale } from "../../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "../ui/dialog";

const SEVERITY_STYLES = {
  info:     { border: "border-l-sky-500",     icon: Sparkles,
                  ring: "bg-sky-50",     iconCls: "text-sky-700" },
  warning:  { border: "border-l-amber-500",   icon: AlertTriangle,
                  ring: "bg-amber-50",   iconCls: "text-amber-700" },
  critical: { border: "border-l-rose-600",    icon: AlertOctagon,
                  ring: "bg-rose-50",     iconCls: "text-rose-700" },
};

function IndustryChip({ ev, t }) {
  if (!ev || ev.industry_median === undefined
      || ev.industry_median === null) return null;
  const tenant = Number(ev.value || 0);
  const median = Number(ev.industry_median);
  if (median === 0) return null;
  const delta = ((tenant - median) / median) * 100;
  const sign = delta >= 0 ? "+" : "−";
  const color = delta >= 0 ? "text-emerald-700" : "text-rose-700";
  return (
    <span
      data-testid="suggestion-vs-industry-chip"
      title={ev.industry_source_reference || ev.industry_source || ""}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5
                          rounded-md bg-muted/40 text-[10px] font-mono
                          tabular-nums ${color}`}
    >
      <span className="uppercase tracking-wide text-muted-foreground">
        {t ? t("suggestions.vs_industry", "vs Branche") : "vs Branche"}
      </span>
      {sign}{Math.abs(delta).toFixed(0)}%
    </span>
  );
}

function EvidenceChip({ ev, t }) {
  if (!ev) return null;
  const positive = (ev.delta_pct || 0) > 0;
  const arrow = positive ? "↑" : (ev.delta_pct < 0 ? "↓" : "·");
  return (
    <div
      data-testid="suggestion-evidence-chip"
      className="inline-flex items-center gap-2 px-2 py-1 rounded-md
                    bg-muted/60 text-[11px] font-mono tabular-nums"
    >
      <span className="uppercase tracking-wide text-muted-foreground">
        {ev.metric}
      </span>
      <span className="font-semibold">{Number(ev.value).toFixed(2)}</span>
      <span className="text-muted-foreground">·</span>
      <span className="text-muted-foreground">
        {Number(ev.comparator).toFixed(2)}
      </span>
      <span className={positive ? "text-emerald-700"
                                  : ev.delta_pct < 0 ? "text-rose-700"
                                  : "text-muted-foreground"}>
        {arrow} {Math.abs(Number(ev.delta_pct || 0)).toFixed(0)}%
      </span>
    </div>
  );
}

function ImpactLLMPanel({ s, t, regenImpactMut }) {
  const llm = (s.impact && s.impact.llm_summary) || null;
  const ready = llm && llm._llm_generated;
  if (!ready) {
    return (
      <div
        data-testid={`suggestion-impact-llm-${s.id}`}
        className="rounded-md border border-dashed border-violet-200
                          bg-violet-50/40 p-2.5 space-y-1.5
                          text-xs text-violet-900"
      >
        <div className="inline-flex items-center gap-1.5 font-medium">
          <Loader2 className="h-3 w-3 animate-spin" />
          {t("suggestions.impact_llm_loading",
              "KI-Forecast wird generiert…")}
        </div>
        <div className="text-[10px] text-muted-foreground italic">
          {t("suggestions.impact_llm_loading_hint",
              "Erscheint nach kurzer Wartezeit. "
              + "Heuristik-Wert bleibt unverändert.")}
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid={`suggestion-impact-llm-${s.id}`}
      className="rounded-md border border-violet-200 bg-violet-50/40
                      p-3 space-y-2 text-xs text-violet-950"
    >
      <div className="flex items-center justify-between gap-2">
        <Badge
          variant="outline"
          className="text-[10px] gap-1 border-violet-300 bg-white
                          text-violet-800"
        >
          <Sparkles className="h-2.5 w-2.5" />
          {t("suggestions.llm_badge", "KI-generiert")}
        </Badge>
        <button
          type="button"
          data-testid={`suggestion-impact-llm-regen-${s.id}`}
          onClick={() => regenImpactMut.mutate()}
          disabled={regenImpactMut.isPending}
          className="text-[10px] text-violet-700 hover:text-violet-900
                          inline-flex items-center gap-1 disabled:opacity-40"
        >
          <RefreshCw className={`h-3 w-3
                                          ${regenImpactMut.isPending
                                              ? "animate-spin" : ""}`} />
          {t("suggestions.impact_llm_regen",
              "Forecast neu generieren")}
        </button>
      </div>
      <p
        className="leading-relaxed"
        data-testid={`suggestion-impact-llm-narrative-${s.id}`}
      >
        {llm.narrative}
      </p>
      {Array.isArray(llm.risk_factors) && llm.risk_factors.length > 0 && (
        <ul
          data-testid={`suggestion-impact-llm-risks-${s.id}`}
          className="list-disc list-outside pl-4 space-y-0.5
                          text-[11px] text-violet-900"
        >
          {llm.risk_factors.map((r, i) => (
            <li key={i}>{r}</li>
          ))}
        </ul>
      )}
      {llm.confidence_explanation && (
        <p
          data-testid={`suggestion-impact-llm-confexp-${s.id}`}
          className="text-[11px] italic text-violet-800
                          border-t border-violet-200/70 pt-1.5"
        >
          {llm.confidence_explanation}
        </p>
      )}
    </div>
  );
}

function SuggestionCard({ s, since, until, qc, t, locale,
                                       isExpanded, onToggleExpand }) {
  const sty = SEVERITY_STYLES[s.severity] || SEVERITY_STYLES.info;
  const SevIcon = sty.icon;
  const [applyOpen, setApplyOpen] = useState(false);
  const [dismissOpen, setDismissOpen] = useState(false);
  const [reason, setReason] = useState("");

  const applyMut = useMutation({
    mutationFn: async () => {
      const r = await api.post(
        `/cockpit/suggestions/${s.id}/apply`,
        null,
        { params: { since, until } });
      return r.data;
    },
    onSuccess: (r) => {
      setApplyOpen(false);
      toast.success(t("suggestions.apply_ok",
                              "Autopilot-Aktion erstellt."));
      qc.invalidateQueries({ queryKey: ["cockpit-suggestions"] });
      qc.invalidateQueries({ queryKey: ["autopilot-actions"] });
    },
    onError: () => {
      toast.error(t("suggestions.apply_failed",
                          "Anwenden fehlgeschlagen."));
    },
  });

  const dismissMut = useMutation({
    mutationFn: async () => {
      const r = await api.post(`/cockpit/suggestions/${s.id}/dismiss`,
                                       { reason: reason || undefined });
      return r.data;
    },
    onSuccess: () => {
      setDismissOpen(false);
      setReason("");
      toast.success(t("suggestions.dismiss_ok",
                              "Empfehlung verworfen."));
      qc.invalidateQueries({ queryKey: ["cockpit-suggestions"] });
    },
    onError: () => {
      toast.error(t("suggestions.dismiss_failed",
                          "Verwerfen fehlgeschlagen."));
    },
  });

  const regenMut = useMutation({
    mutationFn: async () => {
      const r = await api.post(
        `/cockpit/suggestions/${s.id}/regenerate-rationale`,
        null,
        { params: { since, until } });
      return r.data;
    },
    onSuccess: () => {
      toast.success(t("suggestions.regen_ok",
                              "Empfehlung neu generiert."));
      qc.invalidateQueries({ queryKey: ["cockpit-suggestions"] });
    },
    onError: () => {
      toast.error(t("suggestions.regen_failed",
                          "Neu-Generierung fehlgeschlagen."));
    },
  });

  const regenImpactMut = useMutation({
    mutationFn: async () => {
      const r = await api.post(
        `/cockpit/suggestions/${s.id}/regenerate-impact-llm`,
        null,
        { params: { since, until } });
      return r.data;
    },
    onSuccess: () => {
      toast.success(t("suggestions.impact_llm_regen_ok",
                              "Forecast aktualisiert ✓"));
      qc.invalidateQueries({ queryKey: ["cockpit-suggestions"] });
    },
    onError: () => {
      toast.error(t("suggestions.impact_llm_regen_failed",
                          "Forecast-Aktualisierung fehlgeschlagen."));
    },
  });

  const isLLM = !!s._llm_generated;
  const isPending = !!s._llm_pending && !isLLM;

  const headline = s.headline?.[locale] || s.headline?.en
                          || s.headline?.de || s.kind;
  const rationale = s.rationale?.[locale] || s.rationale?.en
                             || s.rationale?.de || "";

  return (
    <Card
      data-testid={`suggestion-card-${s.id}`}
      className={`border-l-4 ${sty.border} relative overflow-hidden`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start gap-3">
          <div className={`rounded-md p-1.5 ${sty.ring}`}>
            <SevIcon className={`h-4 w-4 ${sty.iconCls}`} />
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle
              className="text-sm leading-tight"
              data-testid={`suggestion-headline-${s.id}`}
            >
              {headline}
            </CardTitle>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="text-[10px]">
                {s.target.kind === "platform"
                    ? t("suggestions.target_platform", "Plattform")
                    : s.target.kind === "campaign"
                    ? t("suggestions.target_campaign", "Kampagne")
                    : s.target.kind}
                {": "}{s.target.display_name}
              </Badge>
              <Badge
                variant="outline"
                className="text-[10px] uppercase"
                data-testid={`suggestion-severity-${s.id}`}
              >
                {s.severity}
              </Badge>
              {isLLM && (
                <Badge
                  variant="outline"
                  data-testid={`suggestion-llm-badge-${s.id}`}
                  className="text-[10px] gap-1 border-violet-300 bg-violet-50
                                text-violet-800"
                >
                  <Sparkles className="h-2.5 w-2.5" />
                  {t("suggestions.llm_badge", "KI-generiert")}
                </Badge>
              )}
              {isPending && (
                <Badge
                  variant="outline"
                  data-testid={`suggestion-llm-pending-${s.id}`}
                  className="text-[10px] gap-1 border-slate-200 bg-slate-50
                                text-slate-600"
                >
                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                  {t("suggestions.llm_pending", "Aktualisiert…")}
                </Badge>
              )}
              <span className="text-[10px] text-muted-foreground">
                {t("suggestions.confidence", "Konfidenz")}{" "}
                {Math.round(s.confidence * 100)}%
              </span>
            </div>
          </div>
          <button
            type="button"
            data-testid={`suggestion-regen-${s.id}`}
            onClick={() => regenMut.mutate()}
            disabled={regenMut.isPending}
            title={t("suggestions.regen", "Neu generieren")}
            className="ml-1 p-1 rounded hover:bg-muted text-muted-foreground
                          hover:text-foreground disabled:opacity-40"
          >
            <RefreshCw className={`h-3.5 w-3.5
                                              ${regenMut.isPending ? "animate-spin" : ""}`} />
          </button>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <p className="text-xs text-muted-foreground leading-relaxed"
            data-testid={`suggestion-rationale-${s.id}`}>
          {rationale}
        </p>
        <EvidenceChip ev={s.evidence} />
        <IndustryChip ev={s.evidence} t={t} />
        {s.impact && (s.impact.predicted_lift_eur !== null
                            && s.impact.predicted_lift_eur !== undefined) ? (
          <>
          <div
            data-testid={`suggestion-impact-${s.id}`}
            title={s.impact.historical_basis
              ? `${s.impact.historical_basis.lookback_days}d / `
                  + `${s.impact.historical_basis.samples} samples`
              : ""}
            className="text-xs font-medium inline-flex items-center gap-2"
          >
            {s.impact.direction === "savings" ? (
              <span className="text-sky-700">
                ↓ €{Math.round(s.impact.predicted_lift_eur)}{" "}
                <span className="text-muted-foreground font-normal">
                  {t("suggestions.impact_savings_suffix", "gespart")}
                </span>
              </span>
            ) : s.impact.direction === "lift" ? (
              <span className="text-emerald-700">
                ↑ +€{Math.round(s.impact.predicted_lift_eur)}{" "}
                <span className="text-muted-foreground font-normal">
                  {t("suggestions.impact_lift_suffix", "erwartet")}
                </span>
              </span>
            ) : (
              <span className="text-muted-foreground">
                {t("suggestions.impact_neutral", "Keine Auswirkung")}
              </span>
            )}
            {Array.isArray(s.impact.confidence_interval_eur) && (
              <span className="text-[10px] text-muted-foreground font-mono">
                ±{Math.round(
                    Math.abs(s.impact.confidence_interval_eur[1]
                                - s.impact.confidence_interval_eur[0]) / 2)}€
              </span>
            )}
            <button
              type="button"
              data-testid={`suggestion-impact-expand-${s.id}`}
              onClick={() => onToggleExpand && onToggleExpand(s.id)}
              className="ml-1 text-[10px] text-muted-foreground
                              hover:text-foreground underline-offset-2
                              hover:underline inline-flex items-center gap-0.5"
            >
              {isExpanded
                ? t("suggestions.impact_hide_details", "Details ausblenden")
                : t("suggestions.impact_show_details", "Details anzeigen")}
              {isExpanded
                ? <ChevronUp className="h-3 w-3" />
                : <ChevronDown className="h-3 w-3" />}
            </button>
          </div>
          {isExpanded && (
            <ImpactLLMPanel
              s={s} t={t} qc={qc}
              regenImpactMut={regenImpactMut}
            />
          )}
          </>
        ) : s.impact ? (
          <div
            data-testid={`suggestion-impact-${s.id}`}
            className="text-xs text-muted-foreground italic"
          >
            {t("suggestions.impact_unknown", "Auswirkung unklar")}
          </div>
        ) : null}
        <div className="flex items-center justify-end gap-2 pt-1">
          <Button
            variant="outline" size="sm"
            data-testid={`suggestion-dismiss-${s.id}`}
            onClick={() => setDismissOpen(true)}
          >
            <X className="h-3 w-3 mr-1" />
            {t("suggestions.dismiss", "Verwerfen")}
          </Button>
          <Button
            variant="default" size="sm"
            data-testid={`suggestion-apply-${s.id}`}
            onClick={() => setApplyOpen(true)}
          >
            <Check className="h-3 w-3 mr-1" />
            {t("suggestions.apply", "Anwenden")}
          </Button>
        </div>
      </CardContent>

      {/* Apply confirmation modal */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent
          data-testid={`suggestion-apply-modal-${s.id}`}
          className="max-w-md"
        >
          <DialogHeader>
            <DialogTitle>
              {t("suggestions.confirm_apply",
                  "Empfehlung anwenden?")}
            </DialogTitle>
            <DialogDescription>
              {t("suggestions.confirm_apply_body",
                  "Die vorgeschlagene Aktion wird als Autopilot-Aktion "
                  + "angelegt und durchläuft die normalen Genehmigungsschritte.")}
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs space-y-2 bg-muted/40 p-3 rounded-md">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("suggestions.proposed_action", "Geplante Aktion")}
              </span>
              <span className="font-mono"
                    data-testid={`suggestion-action-kind-${s.id}`}>
                {s.proposed_action.kind}
                {s.proposed_action.delta_pct
                  && ` (${s.proposed_action.delta_pct > 0 ? "+" : ""}${s.proposed_action.delta_pct}%)`}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("suggestions.target", "Ziel")}
              </span>
              <span className="font-mono">
                {s.target.kind}/{s.target.display_name}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyOpen(false)}>
              {t("common.cancel", "Abbrechen")}
            </Button>
            <Button
              data-testid={`suggestion-apply-confirm-${s.id}`}
              onClick={() => applyMut.mutate()}
              disabled={applyMut.isPending}
            >
              {applyMut.isPending
                  ? t("suggestions.applying", "Wende an…")
                  : t("suggestions.apply", "Anwenden")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dismiss with reason modal */}
      <Dialog open={dismissOpen} onOpenChange={setDismissOpen}>
        <DialogContent
          data-testid={`suggestion-dismiss-modal-${s.id}`}
          className="max-w-md"
        >
          <DialogHeader>
            <DialogTitle>
              {t("suggestions.confirm_dismiss", "Empfehlung verwerfen?")}
            </DialogTitle>
            <DialogDescription>
              {t("suggestions.confirm_dismiss_body",
                  "Diese Empfehlung wird für den aktuellen Zeitraum nicht "
                  + "mehr angezeigt.")}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid={`suggestion-dismiss-reason-${s.id}`}
            placeholder={t("suggestions.dismiss_reason_placeholder",
                                  "Grund (optional)")}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDismissOpen(false)}>
              {t("common.cancel", "Abbrechen")}
            </Button>
            <Button
              variant="destructive"
              data-testid={`suggestion-dismiss-confirm-${s.id}`}
              onClick={() => dismissMut.mutate()}
              disabled={dismissMut.isPending}
            >
              {dismissMut.isPending
                  ? t("suggestions.dismissing", "Verwerfe…")
                  : t("suggestions.dismiss", "Verwerfen")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function CockpitSuggestions({ since, until }) {
  const t = useT();
  const { locale } = useLocale();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [sortMode, setSortMode] = useState("severity");
  const [llmExpandedIds, setLlmExpandedIds] = useState(() => new Set());

  const includeLlmImpact = llmExpandedIds.size > 0;
  const toggleLlmExpand = React.useCallback((id) => {
    setLlmExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ["cockpit-suggestions", since, until, includeLlmImpact],
    queryFn: async () => {
      const r = await api.get("/cockpit/suggestions", {
        params: { since, until,
                       ...(includeLlmImpact
                            ? { include_llm_impact: true } : {}) },
      });
      return r.data;
    },
    staleTime: 5 * 60 * 1000,
    // When LLM panels are open and a row is still server-side pending,
    // poll briefly so the narrative surfaces on first cache write.
    refetchInterval: (q) => {
      if (!includeLlmImpact) return false;
      const ss = q.state.data?.suggestions || [];
      const pending = ss.some((s) => {
        const llm = s.impact?.llm_summary;
        return llm && llm._llm_pending && !llm._llm_generated
          && llmExpandedIds.has(s.id);
      });
      return pending ? 2500 : false;
    },
  });

  const suggestions = React.useMemo(() => {
    const raw = data?.suggestions || [];
    if (sortMode !== "impact") return raw;
    // Stable sort by |predicted_lift_eur| desc; null/indeterminate last.
    const score = (s) => {
      const v = s?.impact?.predicted_lift_eur;
      if (v === null || v === undefined) return -Infinity;
      return Math.abs(Number(v));
    };
    return [...raw].sort((a, b) => score(b) - score(a));
  }, [data, sortMode]);
  const visible = expanded ? suggestions : suggestions.slice(0, 3);
  const hidden = Math.max(0, suggestions.length - 3);

  return (
    <section
      data-testid="cockpit-suggestions-section"
      className="space-y-3"
    >
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Lightbulb className="h-4 w-4" />
            {t("suggestions.heading", "Empfohlene Aktionen")}
          </h2>
          <p className="text-xs text-muted-foreground">
            {t("suggestions.subtitle",
                "Evidenzbasierte Vorschläge aus aktueller Cockpit-Performance.")}
          </p>
        </div>
        {data?.meta?.total_suggestions > 0 && (
          <div className="flex items-center gap-3">
            <label className="text-xs text-muted-foreground inline-flex
                                      items-center gap-1.5">
              <ArrowUpDown className="h-3 w-3" />
              <span className="hidden sm:inline">
                {t("suggestions.sort_label", "Sortieren")}:
              </span>
              <select
                data-testid="suggestions-sort"
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value)}
                className="bg-background border border-input rounded-md
                                px-1.5 py-0.5 text-xs tabular-nums
                                focus:outline-none focus:ring-1
                                focus:ring-ring"
              >
                <option value="severity">
                  {t("suggestions.sort_by_severity", "Nach Schweregrad")}
                </option>
                <option value="impact">
                  {t("suggestions.sort_by_impact", "Nach Auswirkung")}
                </option>
              </select>
            </label>
            <span className="text-xs text-muted-foreground tabular-nums"
                  data-testid="suggestions-count">
              {data.meta.total_suggestions}
              {" · "}
              {data.meta.applicable_to_autopilot}{" "}
              {t("suggestions.autopilot_compat", "Autopilot")}
            </span>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="text-xs text-muted-foreground"
              data-testid="cockpit-suggestions-loading">
          {t("common.loading", "Lädt…")}
        </div>
      ) : suggestions.length === 0 ? (
        <Card data-testid="cockpit-suggestions-empty">
          <CardContent className="py-6 text-center text-sm text-muted-foreground">
            <Sparkles className="h-5 w-5 mx-auto mb-2 text-emerald-600" />
            {t("suggestions.empty",
                "Alles im grünen Bereich. Keine Empfehlungen für diesen Zeitraum.")}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {visible.map((s) => (
              <SuggestionCard
                key={s.id} s={s} since={since} until={until}
                qc={qc} t={t} locale={locale}
                isExpanded={llmExpandedIds.has(s.id)}
                onToggleExpand={toggleLlmExpand}
              />
            ))}
          </div>
          {hidden > 0 && (
            <button
              type="button"
              data-testid="suggestions-expand-toggle"
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-muted-foreground hover:text-foreground
                            inline-flex items-center gap-1"
            >
              {expanded ? (
                <><ChevronUp className="h-3 w-3" />
                  {t("suggestions.show_less", "Weniger anzeigen")}</>
              ) : (
                <><ChevronDown className="h-3 w-3" />
                  + {hidden}{" "}
                  {t("suggestions.more", "weitere")}</>
              )}
            </button>
          )}
        </>
      )}
    </section>
  );
}
